import axios, { AxiosInstance, AxiosError, InternalAxiosRequestConfig } from 'axios';
import { tenantStorage } from '../utils/storage';
import { ApiError, toApiError } from './api-error';

export { ApiError } from './api-error';

// Create axios instance with base configuration.
// VITE_API_URL is REQUIRED at build time. The old behaviour fell back to a
// hardcoded prod host ("https://api.chosenonearth.com"), which meant every
// mis-configured build silently hit production. Now the module throws at
// load time so the misconfig surfaces during build/CI smoke instead.
const resolveApiBase = (): string => {
  const envUrl = import.meta.env.VITE_API_URL;
  if (!envUrl || typeof envUrl !== 'string' || envUrl.trim() === '') {
    throw new Error(
      'VITE_API_URL is required. Set it in .env.production / .env.local — there is no fallback.',
    );
  }
  const cleanUrl = envUrl.replace(/\/$/, '');
  return cleanUrl.endsWith('/api') ? cleanUrl : `${cleanUrl}/api`;
};

// Bare host (no trailing /api) — for the rare callers (Stripe confirm-payment,
// AffiliateTracker pixel) that prepend their own path.
export const apiHostBase = (): string => {
  const envUrl = import.meta.env.VITE_API_URL;
  if (!envUrl || typeof envUrl !== 'string' || envUrl.trim() === '') {
    throw new Error(
      'VITE_API_URL is required. Set it in .env.production / .env.local — there is no fallback.',
    );
  }
  return envUrl.replace(/\/$/, '');
};

const api: AxiosInstance = axios.create({
  baseURL: resolveApiBase(),
  timeout: 15000,
  headers: {
    'Content-Type': 'application/json',
  },
});

const getOrderLookupEmail = (): string | undefined => {
  try {
    const checkoutDataRaw =
      tenantStorage.getItem('checkout_form_data') ||
      tenantStorage.getItem('inline_checkout_form_data');
    if (!checkoutDataRaw) {
      return undefined;
    }

    const checkoutData = JSON.parse(checkoutDataRaw);
    const email = checkoutData?.contactInfo?.email;
    return typeof email === 'string' && email.trim() ? email.trim() : undefined;
  } catch {
    return undefined;
  }
};

import { readAttribution } from '../utils/attribution';

// Request interceptor — tenant header + attribution read.
//
// Note: `writeAttribution(?ref=…)` is NOT fired here. Previously this
// interceptor parsed `window.location.search` and wrote on every API call,
// so a single page-load with `?ref=X` re-stamped attribution N times
// (once per request). Attribution is now written once per navigation by
// the AffiliateTracker / usePageTracking route hook — search for
// `writeAttribution` to find the canonical call site.
api.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    // X-Tenant-Domain header is DELIBERATELY NOT sent. Backend hard-rejects
    // any request carrying X-Tenant-ID / X-Tenant-Domain with HTTP 400
    // "Forbidden header present" — tenant context is resolved server-side
    // from the request `Host` / `Origin` header and the `?domain=` query
    // string on public-storefront routes. Sending the header here used to
    // break /storefront/config, /tenant/tax-config, /analytics/page-view
    // on every storefront the moment the backend tightened middleware.
    // See affiliate-backend/src/common/middleware/tenant-context.middleware.ts:49-62.

    // Apply attribution if still within the 30-day window.
    const affiliateId = readAttribution();
    if (affiliateId && config.headers) {
      config.headers['X-Affiliate-Id'] = affiliateId;
    }

    // Auto-inject websiteId on /store/products GETs so per-website filtering
    // applies even when callers bypass the productsAPI helper. The id comes
    // EXCLUSIVELY from `currentWebsiteId` (set by TenantContext via
    // setApiWebsiteId). Window globals are no longer consulted — they were
    // a parallel write path that bypassed the React state and could be
    // tampered with by any script on the page.
    try {
      const url = String(config.url || '');
      const method = String(config.method || 'get').toLowerCase();
      if (method === 'get' && url.includes('/store/products')) {
        const params = (config.params = config.params || {});
        if (params.websiteId == null && params.websiteId !== 0) {
          const wid = getStorefrontWebsiteId();
          if (typeof wid === 'number' && Number.isFinite(wid)) {
            params.websiteId = wid;
          }
        }
        if (params.tenantId == null && params.tenantUuid == null) {
          const tid = getStorefrontTenantId();
          if (tid) params.tenantId = tid;
        }
      }
    } catch { /* ignore */ }

    return config;
  },
  (error: AxiosError) => {
    console.error('Request interceptor error:', error);
    return Promise.reject(error);
  }
);

// Response interceptor — normalize every rejection into ApiError so callers
// branch on `statusCode` / `code` instead of poking at axios internals.
// The top-level ErrorBoundary in App.tsx catches anything that escapes a
// caller's own .catch().
api.interceptors.response.use(
  (response) => response,
  (error: AxiosError) => {
    const apiError = toApiError(error);
    if (import.meta.env.DEV) {
      // eslint-disable-next-line no-console
      console.error(
        `[ApiError ${apiError.statusCode}] ${apiError.message}`,
        apiError.details ?? '',
      );
    }
    return Promise.reject(apiError);
  }
);

// In-flight + short-TTL dedupe for the /store/products list endpoint.
// Many sections (ProductsSection, Recommendations, CartDrawerUpsell, etc.)
// mount in the same tick and each fire the same GET. Audit observed 9× per
// single accent edit in builder preview. We collapse identical requests into
// one network call while still resolving every caller with its own response.
const STOREFRONT_LIST_PATH = '/store/products';
const DEDUPE_TTL_MS = 1500;
const inFlight = new Map<string, Promise<any>>();
const recentCache = new Map<string, { value: any; expiresAt: number }>();

const stableParamsKey = (params: any): string => {
  if (!params || typeof params !== 'object') return '';
  const keys = Object.keys(params).sort();
  return keys.map((k) => `${k}=${params[k] ?? ''}`).join('&');
};

const originalGet = api.get.bind(api);
api.get = ((url: string, config?: any) => {
  // Only dedupe the storefront list endpoint, not /:id or sub-routes.
  if (typeof url !== 'string' || url !== STOREFRONT_LIST_PATH) {
    return originalGet(url, config);
  }
  const params = config?.params;
  const key = `${url}?${stableParamsKey(params)}`;

  const cached = recentCache.get(key);
  if (cached && cached.expiresAt > Date.now()) {
    return Promise.resolve(cached.value);
  }

  const existing = inFlight.get(key);
  if (existing) return existing;

  const promise = originalGet(url, config)
    .then((response) => {
      recentCache.set(key, { value: response, expiresAt: Date.now() + DEDUPE_TTL_MS });
      return response;
    })
    .finally(() => {
      inFlight.delete(key);
    });
  inFlight.set(key, promise);
  return promise;
}) as typeof api.get;

// Helper function to handle API errors in components
export const handleApiError = (error: unknown): string => {
  if (axios.isAxiosError(error)) {
    const axiosError = error as AxiosError<any>;

    if (axiosError.response?.data?.message) {
      return axiosError.response.data.message;
    }

    if (axiosError.response?.status === 404) {
      return 'Resource not found';
    }

    if (axiosError.response?.status === 401) {
      return 'Please log in to continue';
    }

    if (axiosError.response?.status === 403) {
      return 'You do not have permission to access this resource';
    }

    if (axiosError.message === 'Network Error') {
      return 'Network error. Please check your connection';
    }

    return axiosError.message || 'An error occurred';
  }

  return 'An unexpected error occurred';
};

// ============================================
// API Services Exports
// ============================================

/**
 * Products API
 */

/**
 * M39: websiteId source-of-truth.
 *
 * Canonical source is `currentWebsiteId` — TenantContext calls
 * `setApiWebsiteId(...)` whenever it updates its storefront/tenant
 * snapshot. The earlier `window.__STOREFRONT_CONFIG__` /
 * `__STOREFRONT_CONFIG_LATEST__` fallback was removed: it was a parallel
 * write path that bypassed React state and could be tampered with by any
 * script on the page (which was flagged as a low-grade XSS-amplification
 * surface in the audit). If `currentWebsiteId` is unset, calls go
 * without the websiteId header — server scopes by tenant anyway.
 *
 * Function declaration (not `const = () =>`) so the interceptor above
 * can reference it before its lexical position in this file.
 */
let currentWebsiteId: number | undefined = undefined;

export const setApiWebsiteId = (id: number | undefined): void => {
  currentWebsiteId = typeof id === 'number' && Number.isFinite(id) ? id : undefined;
};

function getStorefrontWebsiteId(): number | undefined {
  return typeof currentWebsiteId === 'number' ? currentWebsiteId : undefined;
}

// Tenant scope for /store/products (the backend REQUIRES tenantId/tenantUuid —
// it has no domain fallback). TenantContext calls setApiTenantId with the
// tenant UUID; the request interceptor injects it so services that hit
// /store/products without threading tenantId still resolve the right catalog.
let currentTenantId: string | undefined = undefined;

export const setApiTenantId = (id: string | number | undefined | null): void => {
  currentTenantId = id != null && String(id).length > 0 ? String(id) : undefined;
};

function getStorefrontTenantId(): string | undefined {
  return currentTenantId;
}

export const productsAPI = {
  getAll: async (tenantId: string | number) => {
    const response = await api.get('/store/products', {
      params: { tenantId, websiteId: getStorefrontWebsiteId() }
    });
    return response.data;
  },

  getById: async (id: number, tenantId: string | number) => {
    const response = await api.get(`/store/products/${id}`, {
      params: { tenantId, websiteId: getStorefrontWebsiteId() }
    });
    return response.data;
  },

  search: async (query: string, tenantId: string | number) => {
    const response = await api.get('/store/products/search', {
      params: { query, tenantId, websiteId: getStorefrontWebsiteId() }
    });
    return response.data;
  },

  getByCategory: async (category: string, tenantId: string | number) => {
    const response = await api.get('/store/products', {
      params: { category, tenantId, websiteId: getStorefrontWebsiteId() }
    });
    return response.data;
  },

  getOptions: async (productId: number) => {
    const response = await api.get(`/store/products/${productId}/options`);
    return response.data;
  },

  getFBT: async (productId: number) => {
    const response = await api.get(
      `/store/products/${productId}/frequently-bought-together`,
    );
    return response.data as {
      items: Array<{
        id: number;
        name: string;
        slug: string;
        priceInt: number;
        currency: string;
        imageUrl: string | null;
      }>;
    };
  },

  /**
   * Merchant bundle offers that include this product, with every member
   * resolved. Prices are a preview — checkout re-derives them server-side.
   */
  getBundles: async (productId: number) => {
    const response = await api.get(`/store/products/${productId}/bundles`);
    return response.data as {
      bundles: Array<{
        id: string;
        name: string;
        description?: string;
        bundlePriceInt: number;
        fullPriceInt: number;
        savingsInt: number;
        items: Array<{
          id: number;
          name: string;
          slug: string;
          priceInt: number;
          currency: string;
          imageUrl: string | null;
        }>;
      }>;
    };
  },
};

/**
 * Customer-facing capture forms: newsletter, back-in-stock, SMS opt-in,
 * exit-intent. Single endpoint, dedup'd server-side.
 */
export const capturesAPI = {
  submit: async (data: {
    type: 'newsletter' | 'back_in_stock' | 'sms' | 'exit_intent';
    email?: string;
    phone?: string;
    productId?: number;
    context?: Record<string, any>;
  }): Promise<{ ok: boolean; reason?: string }> => {
    const response = await api.post('/store/captures', data);
    return response.data;
  },
};

/**
 * Orders API (using checkout endpoint)
 */
export const ordersAPI = {
  create: async (orderData: any) => {
    const response = await api.post('/checkout', orderData);
    return response.data;
  },

  getById: async (id: string | number) => {
    // If numeric, it's an order ID (Stripe orders), otherwise it's a bill ID (Billplz)
    const isOrderId = typeof id === 'number' || /^\d+$/.test(String(id));
    const endpoint = isOrderId
      ? `/checkout/order/${id}`
      : `/checkout/order/by-bill/${id}`; // Use order/by-bill endpoint for full order details
    const response = await api.get(endpoint, {
      params: {
        email: getOrderLookupEmail(),
      },
    });
    return response.data;
  },

  getMyOrders: async (page = 1, limit = 10) => {
    // This endpoint may not exist yet - return empty array for now
    return { data: [], total: 0, page, limit };
  },

  cancel: async (orderId: string, reason?: string) => {
    // Cancel functionality not implemented yet
    throw new Error('Order cancellation not implemented');
  }
};

/**
 * Discounts API
 */
export const discountsAPI = {
  validate: async (code: string, orderAmountInt: number, productId?: number, customerEmail: string = '') => {
    const response = await api.post('/checkout/validate-discount', {
      code,
      orderAmountInt,
      productId,
      customerEmail,
    });
    return response.data;
  },
};

/**
 * Affiliates API
 */
export const affiliatesAPI = {
  getByCode: async (code: string) => {
    const response = await api.get(`/affiliates/by-code/${code}`);
    return response.data;
  },

  trackClick: async (refCode: string, productSlug?: string, metadata?: any) => {
    const response = await api.get('/t', {
      params: {
        ref: refCode,
        product: productSlug,
        ...metadata
      }
    });
    return response.data;
  }
};

/**
 * Pages API
 */
export const pagesAPI = {
  getPublished: async () => {
    const response = await api.get('/public/pages');
    return response.data;
  },
};

/**
 * Tenant API
 */
export const tenantAPI = {
  getByDomain: async (domain: string) => {
    const response = await api.get('/public/tenant/config', {
      params: { domain }
    });
    return response.data;
  },

  getConfig: async (domain: string) => {
    const response = await api.get('/public/tenant/config', {
      params: { domain }
    });
    return response.data;
  }
};

// Export configured axios instance
export default api;
